import React, { useEffect, useMemo, useState } from 'react';
import { AuthBridgeProvider } from './AuthSharedContext';
import {
  mergeSnapshotWithFirebaseUser,
  readAuthShellSnapshot,
  writeAuthShellSnapshot
} from '../utils/authShellSnapshot';
import { normalizeAppPath } from '../utils/appRoutes';

const HOME_AUTH_IDLE_TIMEOUT_MS = 2200;
const HOME_AUTH_SNAPSHOT_TIMEOUT_MS = 900;
const HOME_AUTH_INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'focusin'];

const isDeferredPublicHomeBootstrap = () => {
  if (typeof window === 'undefined') return false;
  return normalizeAppPath(window.location.pathname) === '/';
};

const scheduleDeferredBootstrap = (callback, timeoutMs) => {
  if (typeof window === 'undefined') {
    callback();
    return () => {};
  }

  if ('requestIdleCallback' in window) {
    const idleId = window.requestIdleCallback(() => callback(), { timeout: timeoutMs });
    return () => window.cancelIdleCallback(idleId);
  }

  const timeoutId = window.setTimeout(() => callback(), timeoutMs);
  return () => window.clearTimeout(timeoutId);
};

const resolvePublicUserRecord = async (baseUser, db, firestoreModule) => {
  if (!baseUser?.uid || !db) return baseUser;

  try {
    const { doc, getDoc } = firestoreModule;
    const userSnapshot = await getDoc(doc(db, 'users', baseUser.uid));
    if (!userSnapshot.exists()) return baseUser;

    const data = userSnapshot.data() || {};
    return {
      ...baseUser,
      credits: Number.isFinite(Number(data.credits)) ? Number(data.credits) : baseUser.credits,
      totalProcessed: Number.isFinite(Number(data.totalProcessed)) ? Number(data.totalProcessed) : baseUser.totalProcessed,
      plan: String(data.plan || baseUser.plan || 'free').trim() || 'free',
      subscriptionStatus: String(data.subscriptionStatus || baseUser.subscriptionStatus || 'inactive').trim() || 'inactive',
      isAdmin: Boolean(data.isAdmin ?? baseUser.isAdmin),
      willRenew: Boolean(data.willRenew ?? baseUser.willRenew),
      freeByokDailyUsage: data.freeByokDailyUsage || baseUser.freeByokDailyUsage || null,
      currentPeriodEndDate: data.currentPeriodEndDate || baseUser.currentPeriodEndDate || null,
      subscriptionStartDate: data.subscriptionStartDate || baseUser.subscriptionStartDate || null
    };
  } catch {
    return baseUser;
  }
};

export const PublicShellAuthProvider = ({ children, deferHomeBootstrap = true }) => {
  const [authState, setAuthState] = useState(() => ({
    user: null,
    loading: true,
    authResolved: false,
    error: null,
    isAdmin: false
  }));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let cancelled = false;
    let unsubscribeFromAuth = null;
    let authBootstrapStarted = false;
    let authObserverVersion = 0;
    let cancelDeferredBootstrap = () => {};
    const snapshotUser = readAuthShellSnapshot();
    const shouldDeferBootstrap = deferHomeBootstrap && isDeferredPublicHomeBootstrap();

    const syncSnapshotState = (loading) => {
      setAuthState({
        user: snapshotUser || null,
        loading,
        authResolved: false,
        error: null,
        isAdmin: Boolean(snapshotUser?.isAdmin)
      });
    };

    const detachInteractionListeners = () => {
      HOME_AUTH_INTERACTION_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, startBootstrap, true);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };

    const resolveAuthState = async () => {
      if (!shouldDeferBootstrap) {
        syncSnapshotState(true);
      }

      try {
        const [{ publicAuth }, authModule] = await Promise.all([
          import('../config/publicFirebaseAuth'),
          import('firebase/auth')
        ]);

        if (cancelled) return;

        unsubscribeFromAuth = authModule.onAuthStateChanged(publicAuth, (firebaseUser) => {
          const observerVersion = ++authObserverVersion;
          const isStale = () => cancelled || observerVersion !== authObserverVersion;
          if (isStale()) return;

          if (!firebaseUser?.uid) {
            writeAuthShellSnapshot(null);
            setAuthState({
              user: null,
              loading: false,
              authResolved: true,
              error: null,
              isAdmin: false
            });
            return;
          }

          const baseUser = mergeSnapshotWithFirebaseUser(snapshotUser, firebaseUser);
          writeAuthShellSnapshot(baseUser);
          setAuthState({
            user: baseUser,
            loading: false,
            authResolved: true,
            error: null,
            isAdmin: Boolean(baseUser?.isAdmin)
          });

          void Promise.all([
            import('../config/firebase'),
            import('firebase/firestore')
          ]).then(async ([{ db }, firestoreModule]) => {
            const resolvedUser = await resolvePublicUserRecord(baseUser, db, firestoreModule);
            if (isStale()) return;

            writeAuthShellSnapshot(resolvedUser);
            setAuthState({
              user: resolvedUser,
              loading: false,
              authResolved: true,
              error: null,
              isAdmin: Boolean(resolvedUser?.isAdmin)
            });
          }).catch((error) => {
            if (isStale()) return;
            setAuthState((prev) => ({
              ...prev,
              loading: false,
              authResolved: true,
              error,
              isAdmin: Boolean(prev.user?.isAdmin)
            }));
          });
        }, (error) => {
          authObserverVersion += 1;
          if (cancelled) return;
          setAuthState({
            user: null,
            loading: false,
            authResolved: true,
            error,
            isAdmin: false
          });
        });
      } catch (error) {
        if (cancelled) return;

        setAuthState({
          user: null,
          loading: false,
          authResolved: true,
          error,
          isAdmin: false
        });
      }
    };

    function startBootstrap() {
      if (authBootstrapStarted || cancelled) return;
      authBootstrapStarted = true;
      cancelDeferredBootstrap();
      detachInteractionListeners();
      void resolveAuthState();
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        startBootstrap();
      }
    }

    if (shouldDeferBootstrap) {
      syncSnapshotState(false);
      HOME_AUTH_INTERACTION_EVENTS.forEach((eventName) => {
        window.addEventListener(eventName, startBootstrap, { capture: true, passive: true });
      });
      document.addEventListener('visibilitychange', handleVisibilityChange);
      cancelDeferredBootstrap = scheduleDeferredBootstrap(
        startBootstrap,
        snapshotUser?.uid ? HOME_AUTH_SNAPSHOT_TIMEOUT_MS : HOME_AUTH_IDLE_TIMEOUT_MS
      );
    } else {
      startBootstrap();
    }

    return () => {
      cancelled = true;
      authObserverVersion += 1;
      cancelDeferredBootstrap();
      detachInteractionListeners();
      unsubscribeFromAuth?.();
    };
  }, [deferHomeBootstrap]);

  const value = useMemo(() => ({
    ...authState,
    isAdmin: Boolean(authState.user?.isAdmin ?? authState.isAdmin)
  }), [authState]);

  return (
    <AuthBridgeProvider value={value}>
      {children}
    </AuthBridgeProvider>
  );
};

export default PublicShellAuthProvider;
